import { Card, CardBody, CardHeader, CardTitle, Progress } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, StatusPill, EmptyState } from '@vnetwork/ui/data';
import { Activity } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { useServiceConfigoptions, type ServiceConfigOption, type ServiceDetail } from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** Ratio of used/total for a config option, clamped to [0,1]. Null when total is unknown. */
function usageRatio(o: ServiceConfigOption): number | null {
  const total = o.commitment_value ?? 0;
  if (!total || total <= 0) return null;
  const used = o.commitment_used ?? 0;
  return Math.min(Math.max(used / total, 0), 1);
}

/** Progress tone by fill ratio: ≥90% danger, ≥75% warning, else primary. */
function fillTone(ratio: number | null): 'primary' | 'warning' | 'danger' {
  if (ratio != null && ratio >= 0.9) return 'danger';
  if (ratio != null && ratio >= 0.75) return 'warning';
  return 'primary';
}

const numFmt = (v?: number | null) => (v == null ? '—' : v.toLocaleString('en-US'));

/** A labelled usage row: name, prepaid/postpaid pill, progress bar, used/total/remaining. */
function UsageRow({ option, currency = 'VND' }: { option: ServiceConfigOption; currency?: string }) {
  const ratio = usageRatio(option);
  const total = option.commitment_value ?? 0;
  const used = option.commitment_used ?? 0;
  const remaining = option.commitment_available ?? (total ? total - used : null);
  const unit = option.unit ? ` ${option.unit}` : '';
  const pct = ratio != null ? `${Math.round(ratio * 100)}%` : null;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)', paddingBlock: 'var(--vn-space-3)', borderBottom: '1px solid var(--vn-border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{option.name}</span>
        <StatusPill tone={option.commitment_prepaid ? 'success' : 'neutral'}>
          {option.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
        </StatusPill>
      </div>

      <Progress
        value={ratio != null ? Math.round(ratio * 100) : 0}
        max={100}
        tone={fillTone(ratio)}
        size="sm"
        aria-label={`${option.name} usage`}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
        <span>
          Used <strong style={{ color: 'var(--vn-fg-default)' }}>{numFmt(used)}{unit}</strong> / {numFmt(total)}{unit}
          {pct ? ` (${pct})` : ''}
        </span>
        <span>Remaining <strong style={{ color: 'var(--vn-fg-default)' }}>{numFmt(remaining)}{unit}</strong></span>
        {option.commitment_price_total ? <span>Value {formatMoney(option.commitment_price_total, currency)}</span> : null}
      </div>
    </div>
  );
}

/**
 * Monitoring (usage) tab — a per-config-option usage view sourced from
 * `/services/:id/configoptions` (real, safe). For each option we render a
 * commitment usage bar (`commitment_used` / `commitment_value`) that turns
 * warning/danger as it approaches the committed limit. The `/analytics`
 * time-series endpoint is intentionally NOT used here (it 500s on wrong
 * per-template `type` values); detailed charts live in the provider console.
 */
export function ServiceMonitoringTab({ detail }: { detail: ServiceDetail }) {
  const { data, isLoading, isError, refetch } = useServiceConfigoptions(detail.service_id);

  if (isError) return <TabError title="Failed to load usage" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={4} />;

  const options = data?.results ?? [];
  const currency = detail.service_currency || 'VND';

  if (options.length === 0) {
    return (
      <Card flat>
        <CardBody>
          <EmptyState icon={<Activity size={24} />} title="No usage data" description="This service has no metered configuration items to monitor." />
        </CardBody>
      </Card>
    );
  }

  const nearLimit = options.filter((o) => {
    const r = usageRatio(o);
    return r != null && r >= 0.75;
  }).length;

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Usage</CardTitle>
      </CardHeader>
      <CardBody>
        <StatGroup columns={2} style={{ marginBottom: 'var(--vn-space-4)' }}>
          <Stat label="Metered items" value={options.length.toLocaleString('en-US')} dotColor="var(--vn-primary-solid)" />
          <Stat
            label="Near limit"
            value={nearLimit.toLocaleString('en-US')}
            dotColor={nearLimit > 0 ? 'var(--vn-warning-solid)' : 'var(--vn-success-solid)'}
            hint="≥ 75% of committed"
          />
        </StatGroup>

        <div>
          {options.map((o) => (
            <UsageRow key={o.service_configoption_id} option={o} currency={currency} />
          ))}
        </div>

        <p style={{ marginTop: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-0)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          Detailed time-series charts are available in the provider console; the analytics pipeline is template-specific.
        </p>
      </CardBody>
    </Card>
  );
}
